import React, { useState, useEffect } from 'react';
import { Menu, X, Zap } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  forceBackground?: boolean;
}

export default function Header({ forceBackground = false }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const showBackground = lastScrollY > 50 || forceBackground || isMenuOpen;

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      } ${
        showBackground 
          ? 'bg-white/80 backdrop-blur-md shadow-[0_8px_30px_rgb(108,99,255,0.1)]' 
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <div className={`w-10 h-10 rounded-xl ${
              showBackground ? 'bg-primary-100' : 'bg-white/10 backdrop-blur-sm'
            } flex items-center justify-center transition-colors`}>
              <Zap className={`h-6 w-6 ${showBackground ? 'text-primary-600' : 'text-white'}`} />
            </div>
            <span className={`ml-2 text-xl font-bold ${
              showBackground ? 'text-gray-900' : 'text-white'
            }`}>
              Kontty
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`transition-colors ${
                showBackground ? 'text-gray-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Início
            </Link>
            <Link 
              to="/sobre" 
              className={`transition-colors ${
                showBackground ? 'text-gray-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Sobre
            </Link>
            <Link 
              to="/precos" 
              className={`transition-colors ${
                showBackground ? 'text-gray-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Preços
            </Link>
            <Link 
              to="/login" 
              className={`transition-colors ${
                showBackground ? 'text-gray-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Login
            </Link>
            <Link 
              to="/cadastro" 
              className={`px-6 py-2 rounded-full transition-all transform hover:scale-105 ${
                showBackground
                  ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-[0_4px_20px_rgb(108,99,255,0.2)]'
                  : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
              }`}
            >
              Começar
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className={showBackground ? 'h-6 w-6 text-gray-900' : 'h-6 w-6 text-white'} />
            ) : (
              <Menu className={showBackground ? 'h-6 w-6 text-gray-900' : 'h-6 w-6 text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4">
            <div className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="text-gray-700 hover:text-primary-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Início
              </Link>
              <Link 
                to="/sobre" 
                className="text-gray-700 hover:text-primary-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Sobre
              </Link>
              <Link 
                to="/precos" 
                className="text-gray-700 hover:text-primary-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Preços
              </Link>
              <Link 
                to="/login" 
                className="text-gray-700 hover:text-primary-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
              <Link 
                to="/cadastro" 
                className="px-6 py-2 rounded-full text-center bg-primary-600 text-white hover:bg-primary-700 transition-all transform hover:scale-105 shadow-[0_4px_20px_rgb(108,99,255,0.2)]"
                onClick={() => setIsMenuOpen(false)}
              >
                Começar
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}